import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bot,
  Sparkles,
  Target,
  Users,
  GraduationCap,
  ClipboardCheck,
  Coins,
  Heart,
  Lock,
  CheckCircle2,
  AlertTriangle,
  Wand2,
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Zap,
  Building2,
  Loader2,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';
import TalentChat from '../../components/chat/TalentChat';
import { supabase } from '../../lib/supabase';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

// — Typography aligned on home / ia / recrutement / externalisation —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass = 'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Section 2 — Constat (3 cartes : pourquoi un coach IA RH) —
const painPoints = [
  {
    icon: AlertTriangle,
    title: "Vous bricolez votre stratégie RH dans Excel et ChatGPT générique",
    description:
      "Les bons réflexes sont éparpillés entre forums, posts LinkedIn, prompts qu'on retape à chaque fois. Aucune mémoire, aucun contexte sur votre marché, votre taille, vos contraintes.",
  },
  {
    icon: Building2,
    title: 'Vous n\'avez pas de DRH dédié — et le recruter coûte 90 K€/an',
    description:
      "Vous avez 30, 50, 200 collaborateurs. Vous prenez les décisions RH stratégiques sur un coin de table, entre deux réunions, sans ressource senior pour challenger.",
  },
  {
    icon: Bot,
    title: "Vous voulez profiter de l'IA, pas reconstruire votre SI RH",
    description:
      "Tous les éditeurs SaaS vous parlent de modules IA à intégrer dans 6 mois. Vous, vous avez besoin d'une réponse argumentée maintenant, sur votre vraie question du jour.",
  },
];

// — Section 3 — 6 domaines couverts par TalentHrAi —
const domains = [
  {
    icon: Target,
    title: 'Stratégie de recrutement',
    description:
      "Cadrage du besoin, choix sourcing vs chasse, calibrage du package, structure d'entretiens, métriques à suivre.",
  },
  {
    icon: Sparkles,
    title: 'Marque employeur',
    description:
      "EVP, ton de voix, contenus à produire, canaux à activer, scénarisation Parcours Découverte côté candidat.",
  },
  {
    icon: GraduationCap,
    title: 'Plan de formation',
    description:
      "Identification des compétences clés, choix entre interne/externe, OPCO, AFEST, financement et calendrier.",
  },
  {
    icon: ClipboardCheck,
    title: 'Onboarding',
    description:
      "Parcours d'intégration jour 1 / semaine 1 / mois 1 / mois 3, points de contrôle, contenus à fournir, signaux d'alerte.",
  },
  {
    icon: Users,
    title: 'Gestion de la performance',
    description:
      "Cadres d'évaluation (OKR, 360, entretiens annuels), feedback continu, plans d'amélioration, gestion des talents.",
  },
  {
    icon: Coins,
    title: 'Politique de rémunération',
    description:
      "Grille salariale, variable, avantages, cohérence interne, lecture marché. Ce que vos pairs PACA proposent réellement.",
  },
];

// — Section 4 — Comment ça marche (3 étapes) —
const howItWorks = [
  {
    icon: ClipboardCheck,
    step: '01',
    title: 'Vous nous dites qui vous êtes',
    description:
      "Un formulaire court (taille de l'entreprise, fonction, sujet prioritaire). Pas pour gater l'accès — pour contextualiser les réponses dès la première question.",
  },
  {
    icon: Lock,
    step: '02',
    title: 'Vous accédez gratuitement à votre coach IA',
    description:
      "Le chatbot s'ouvre directement dans votre navigateur. Aucune installation. Aucun engagement. Vous posez vos questions RH dans votre langage métier.",
  },
  {
    icon: Wand2,
    step: '03',
    title: "Vous obtenez des réponses contextualisées et actionnables",
    description:
      "Pas de blabla générique : des plans d'action structurés, des modèles à adapter, des ordres de grandeur calibrés sur le marché PACA et votre type d'organisation.",
  },
];

// — Section 6 — Différenciateurs (4 raisons) —
const differentiators = [
  {
    icon: ShieldCheck,
    title: 'Construit par un cabinet, pas par un éditeur SaaS',
    description:
      "Notre coach a été nourri par des dizaines de missions de recrutement et d'externalisation RH. Il restitue des réflexes terrain — pas du contenu corporate.",
  },
  {
    icon: Zap,
    title: 'Réponse en 10 secondes, pas en 3 jours',
    description:
      "Avant TalentHrAi : Slack à votre RH externe, attendre la dispo, payer un honoraire. Après : la même qualité de cadrage, instantané.",
  },
  {
    icon: Heart,
    title: 'Gratuit pour vous laisser le temps de l\'apprivoiser',
    description:
      "Aucun paywall, aucune carte bleue. Si vous avez besoin de plus (workflow IA dédié, intégration outils, RAG sur vos données), on en parle quand vous êtes convaincu.",
  },
  {
    icon: Sparkles,
    title: 'Le pont vers notre offre IA RH sur-mesure',
    description:
      "Si vos cas d'usage dépassent ce qu'un assistant générique peut faire, nous construisons votre IA RH dédiée — chatbots, prédictif, scoring, génératif.",
  },
];

// — Formulaire de capture lead natif (remplace l'iframe HubSpot) —
interface LeadFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  role: string;
}

const ROLE_OPTIONS = [
  { value: '', label: 'Sélectionnez…' },
  { value: 'dg', label: 'Dirigeant·e / DG / Fondateur·rice' },
  { value: 'drh', label: 'DRH / RRH' },
  { value: 'rh-ops', label: 'RH opérationnel·le / Recruteur·euse' },
  { value: 'manager', label: 'Manager opérationnel' },
  { value: 'autre', label: 'Autre' },
];

export default function TalentHrAiPage() {
  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState<LeadFormData>({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    role: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  function setField<K extends keyof LeadFormData>(field: K, value: LeadFormData[K]) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    // Validation client
    const { firstName, lastName, email, company, role } = formData;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !company.trim() || !role) {
      setSubmitError('Tous les champs sont requis pour activer votre coach.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setSubmitError("Format email invalide — utilisez votre email professionnel.");
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from('leads').insert([
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim().toLowerCase(),
          company: company.trim(),
          source: 'talent_hr_ai',
          // role stocké dans une colonne text si dispo ; sinon ignoré côté DB
          // (le schéma `leads` accepte ce champ ailleurs dans l'app)
          score: role,
        },
      ]);

      if (error) {
        // On log mais on n'empêche pas l'accès au chatbot — l'objectif est
        // de servir l'utilisateur. L'erreur est tracée pour debug.
        console.error('[talent-hr-ai] Erreur insert lead:', error.message);
      }
      setFormSubmitted(true);
    } catch (err) {
      console.error('[talent-hr-ai] Submit error:', err);
      setSubmitError("Une erreur est survenue. Réessayez ou contactez-nous directement.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout
      title="TalentHrAi · Coach IA gratuit pour la fonction RH | recruteurs.io"
      description="TalentHrAi est un assistant IA gratuit pour les dirigeants et responsables RH de PME : stratégie de recrutement, marque employeur, formation, onboarding, performance, rémunération. Construit par un cabinet — pas par un éditeur SaaS. Réponses contextualisées sur votre marché PACA."
      keywords="coach IA RH, assistant IA recruteurs, talent rh ai, IA pour DRH, chatbot RH gratuit, stratégie de recrutement IA, marque employeur IA, IA pour PME, recruteurs.io"
    >
      <WebPageSchema
        page={{
          title:
            'TalentHrAi · Coach IA gratuit pour la fonction RH | recruteurs.io',
          description:
            "TalentHrAi : coach IA gratuit pour la fonction RH des PME. Stratégie de recrutement, marque employeur, formation, onboarding, performance, rémunération. Réponses contextualisées sur le marché PACA. Construit par recruteurs.io.",
          url: 'https://recruteurs.io/entreprise/talent-rh-ai',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Entreprise', url: 'https://recruteurs.io/entreprise/recrutement' },
          {
            name: 'TalentHrAi',
            url: 'https://recruteurs.io/entreprise/talent-rh-ai',
          },
        ]}
      />

      <HeroTemplateEnts
        page="talent-hr-ai"
        title="Votre coach IA pour la fonction RH."
        eyebrow="TalentHrAi · Assistant IA gratuit · PME PACA"
        description="Stratégie de recrutement, marque employeur, plan de formation, onboarding, gestion de la performance, politique de rémunération. TalentHrAi répond à vos questions RH avec le réflexe d'un cabinet senior — gratuit, contextualisé, instantané. Construit par recruteurs.io, pas par un éditeur SaaS."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Conseil-IA-marseille.webp"
        breadcrumbs={[
          { name: 'Entreprise', href: '/entreprise/recrutement' },
          { name: 'TalentHrAi', href: '/entreprise/talent-rh-ai' },
        ]}
        cta1={{
          label: 'Activer mon coach IA gratuit',
          href: '#talent-hr-ai-form',
        }}
        cta2={{
          label: 'Voir les sujets couverts',
          href: '#talent-hr-ai-domains',
        }}
      />

      {/* SECTION 2 — Le constat (3 pain points) */}
      <SectionBackground page="talent-hr-ai" section="thra-pain" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce que vit une PME aujourd'hui</span>
            <h2 className={h2Class}>
              Vous prenez des décisions RH stratégiques{' '}
              <span className={gradientText}>sans renfort senior.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Trois symptômes qu'on retrouve dans 90 % des PME que nous accompagnons —
              et que TalentHrAi corrige immédiatement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {painPoints.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="rounded-[1.75rem] bg-white p-7 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — 6 domaines couverts */}
      <SectionBackground
        page="talent-hr-ai"
        section="thra-domains"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div
            id="talent-hr-ai-domains"
            className="max-w-4xl mx-auto text-center mb-14 scroll-mt-24"
          >
            <span className={eyebrowClass}>6 sujets, 1 coach</span>
            <h2 className={h2Class}>
              Tout ce qu'un DRH externe vous coûterait,{' '}
              <span className={gradientText}>en 10 secondes par question.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Six sujets de la fonction RH couverts par défaut — avec
              contextualisation sur votre taille, votre secteur, votre marché.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domains.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="rounded-[1.75rem] bg-white p-7 ring-1 ring-neutral-200 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-md shadow-[#d03484]/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Comment ça marche */}
      <SectionBackground
        page="talent-hr-ai"
        section="thra-how"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Comment ça marche</span>
            <h2 className={h2Class}>
              Trois étapes,{' '}
              <span className={gradientText}>moins de 90 secondes.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              De la première visite à la première réponse activable, le chemin
              est court — par design.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {howItWorks.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="relative rounded-[1.75rem] bg-white p-7 ring-1 ring-neutral-200 shadow-sm"
                >
                  <span
                    className={`absolute top-6 right-6 text-3xl font-bold ${gradientText} leading-none`}
                  >
                    {item.step}
                  </span>
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Form gated → Chatbase iframe (logique préservée) */}
      <SectionBackground
        page="talent-hr-ai"
        section="thra-form"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div
            id="talent-hr-ai-form"
            className="max-w-3xl mx-auto scroll-mt-24"
          >
            {!formSubmitted && (
              <>
                <div className="text-center mb-10">
                  <span className={eyebrowClass}>
                    <Lock className="w-4 h-4 mr-2" />
                    Activation gratuite
                  </span>
                  <h2 className={h2Class}>
                    Activez votre coach IA RH{' '}
                    <span className={gradientText}>en moins d'une minute.</span>
                  </h2>
                  <p className={sectionSubtitleClass}>
                    Le formulaire ci-dessous nous sert à contextualiser vos réponses
                    (taille d'entreprise, secteur, sujet prioritaire). Pas de paywall,
                    pas de carte bleue, pas d'engagement.
                  </p>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="rounded-[1.75rem] bg-white ring-1 ring-neutral-200 shadow-sm p-8 md:p-10 space-y-5"
                  noValidate
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#30475e] mb-1.5">
                        Prénom <span className="text-[#d03484]">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setField('firstName', e.target.value)}
                        autoComplete="given-name"
                        disabled={submitting}
                        className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#30475e] mb-1.5">
                        Nom <span className="text-[#d03484]">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setField('lastName', e.target.value)}
                        autoComplete="family-name"
                        disabled={submitting}
                        className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#30475e] mb-1.5">
                      Email professionnel <span className="text-[#d03484]">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setField('email', e.target.value)}
                      autoComplete="email"
                      disabled={submitting}
                      placeholder="prenom.nom@entreprise.com"
                      className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] placeholder:text-neutral-400 outline-none transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#30475e] mb-1.5">
                        Entreprise <span className="text-[#d03484]">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setField('company', e.target.value)}
                        autoComplete="organization"
                        disabled={submitting}
                        className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#30475e] mb-1.5">
                        Votre fonction <span className="text-[#d03484]">*</span>
                      </label>
                      <select
                        value={formData.role}
                        onChange={(e) => setField('role', e.target.value)}
                        disabled={submitting}
                        className="w-full rounded-xl ring-1 ring-neutral-200 focus:ring-2 focus:ring-[#d03484]/40 bg-[#fbfaf8] focus:bg-white px-4 py-2.5 text-sm text-[#30475e] outline-none transition-all"
                      >
                        {ROLE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {submitError && (
                    <div className="rounded-xl bg-red-50 ring-1 ring-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{submitError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-3 text-sm font-bold text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0 whitespace-nowrap"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Activation en cours...
                      </>
                    ) : (
                      <>
                        Activer mon coach IA
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-neutral-500 text-center">
                    En soumettant, vous acceptez d'être recontacté·e par recruteurs.io.
                    Vos données restent en France (Supabase EU) et ne sont jamais vendues.
                  </p>
                </form>

                <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-500">
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-[#d03484]" /> 100 % gratuit
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-[#d03484]" /> Aucun engagement
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-[#d03484]" /> Données RGPD
                  </span>
                </div>
              </>
            )}

            {formSubmitted && (
              <>
                <div className="text-center mb-10">
                  <span className={eyebrowClass}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Coach activé
                  </span>
                  <h2 className={h2Class}>
                    Vous êtes prêt·e.{' '}
                    <span className={gradientText}>Posez votre première question.</span>
                  </h2>
                  <p className={sectionSubtitleClass}>
                    TalentHrAi est ouvert ci-dessous. Démarrez par votre sujet
                    prioritaire — l'assistant adaptera ses réponses au contexte
                    PME que vous venez de partager.
                  </p>
                </div>

                <TalentChat
                  chatbotId="B-yCETKMIBROl19MyTnEz"
                  welcomeMessage="Bonjour ! Je suis TalentHrAi, votre coach IA gratuit pour la fonction RH. Posez-moi votre question prioritaire — stratégie de recrutement, marque employeur, plan de formation, onboarding, performance ou rémunération."
                  placeholder="Ex. : « Comment structurer un onboarding pour mes 3 prochaines embauches ? »"
                  quickPrompts={[
                    "Cadre mon plan de recrutement pour les 6 prochains mois",
                    'Construis-moi une grille salariale cohérente pour ma PME',
                    "Donne-moi un parcours d'onboarding 30/60/90 jours",
                    "Comment activer ma marque employeur sans budget Linkedin Ads ?",
                  ]}
                  height={640}
                />
              </>
            )}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Différenciateurs */}
      <SectionBackground
        page="talent-hr-ai"
        section="thra-diff"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pourquoi celui-ci, pas un autre</span>
            <h2 className={h2Class}>
              ChatGPT générique répond à tout le monde.{' '}
              <span className={gradientText}>TalentHrAi répond à vous.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Quatre choses qui distinguent un coach IA construit par un cabinet
              de recrutement d'un assistant générique grand public.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {differentiators.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="flex gap-4 rounded-[1.75rem] bg-white p-7 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-md shadow-[#d03484]/20">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={h3Class}>{item.title}</h3>
                    <p className={bodyClass}>{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 — Cross-link IA RH sur-mesure */}
      <SectionBackground
        page="talent-hr-ai"
        section="thra-bridge"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-4xl mx-auto rounded-[2rem] bg-gradient-to-br from-[#30475e] to-[#1f3245] p-10 md:p-12 text-white shadow-xl">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#fde7f2]" />
              <span className="text-xs font-semibold uppercase tracking-widest text-[#fde7f2]">
                Quand le coach ne suffit plus
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-5 leading-[1.1] text-white">
              Vos cas d'usage{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fde7f2] to-[#fde2d3]">
                sortent du cadre générique
              </span>{' '}
              ?
            </h2>
            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-3xl">
              TalentHrAi est volontairement généraliste. Si votre besoin réel
              relève du chatbot RH branché à votre SIRH, du scoring sémantique
              de CV sur vos référentiels métier, ou d'un workflow d'IA
              générative dédié — nous le construisons.
            </p>
            <button
              onClick={() => navigate('/entreprise/ia')}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-2 text-sm font-bold whitespace-nowrap text-[#30475e] hover:bg-[#fde7f2] hover:-translate-y-0.5 transition-all shadow-lg"
            >
              Découvrir IA RH sur-mesure
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 8 — CTA final */}
      <SectionBackground
        page="talent-hr-ai"
        section="thra-cta"
        className="bg-white"
      >
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <Bot className="w-10 h-10 text-[#d03484] mx-auto mb-5" />
            <h2 className={h2Class}>
              Cinq minutes pour activer.{' '}
              <span className={gradientText}>Une vie de DRH augmenté.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-8`}>
              Activez TalentHrAi maintenant. Si plus tard vous avez besoin d'un
              regard humain ou d'un projet IA dédié, nous restons à un appel.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="#talent-hr-ai-form"
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Activer mon coach IA gratuit
                <ArrowRight className="w-4 h-4" />
              </a>
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center justify-center gap-2 rounded-2xl ring-1 ring-neutral-200 bg-white px-5 py-2 text-sm font-semibold whitespace-nowrap text-[#30475e] hover:bg-[#fbfaf8] transition-all"
              >
                Parler à un humain · 15 min
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 9 — SEO content */}
      <SectionBackground
        page="talent-hr-ai"
        section="thra-seo"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-3xl mx-auto prose prose-neutral">
            <h2 className={`${h2Class} text-3xl md:text-4xl`}>
              TalentHrAi en quelques mots
            </h2>
            <p className={bodyClass}>
              <strong>TalentHrAi</strong> est l'assistant IA conçu par{' '}
              <strong>recruteurs.io</strong>, cabinet de recrutement basé à
              Marseille, pour répondre aux dirigeants et responsables RH des PME
              qui prennent des décisions stratégiques sans pouvoir mobiliser un
              DRH senior à plein temps.
            </p>
            <p className={`${bodyClass} mt-4`}>
              Là où un assistant grand public restitue des réponses génériques
              compilées depuis l'ensemble du web, TalentHrAi a été nourri par la
              pratique d'un cabinet : missions de recrutement de cadres,
              externalisation RH, conception de Parcours Découverte côté
              candidat, marque employeur scénarisée. Ses réponses portent ce
              vécu — pas juste de la théorie RH.
            </p>
            <p className={`${bodyClass} mt-4`}>
              Six domaines sont couverts par défaut : la stratégie de
              recrutement, la marque employeur, le plan de formation,
              l'onboarding, la gestion de la performance et la politique de
              rémunération. La contextualisation tient compte de la taille de
              votre organisation, du secteur, et lorsque c'est pertinent, du
              marché PACA (Marseille, Aix-en-Provence, Toulon, Nice) sur lequel
              recruteurs.io opère au quotidien.
            </p>
            <p className={`${bodyClass} mt-4`}>
              L'usage est <strong>gratuit et sans engagement</strong>. Pour les
              entreprises qui souhaitent aller plus loin — chatbot connecté à
              leur SIRH, IA prédictive sur la performance, scoring sémantique
              de candidatures, ou IA générative de contenus marque employeur —
              recruteurs.io conçoit des solutions{' '}
              <a
                href="/entreprise/ia"
                className="text-[#d03484] hover:underline font-semibold"
              >
                IA RH sur-mesure
              </a>
              .
            </p>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
